import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Users, 
  Trash2, 
  ArrowLeft,
  Phone,
  Calendar,
  Search,
  Plus,
  Upload,
  RefreshCw
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter,
  DialogDescription
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import { Badge } from "../components/ui/badge";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactsManagement() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [deleteContactId, setDeleteContactId] = useState(null);
  
  // Dialog states
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  
  // Form state
  const [newContact, setNewContact] = useState({ name: "", phone: "" });
  const [dragActive, setDragActive] = useState(false);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/contacts`);
      setContacts(response.data);
      setFilteredContacts(response.data);
    } catch (error) {
      console.error("Error fetching contacts:", error);
      toast.error("Errore nel caricamento dei contatti");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredContacts(contacts);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = contacts.filter(
        (contact) =>
          contact.name.toLowerCase().includes(query) ||
          contact.phone.includes(query)
      );
      setFilteredContacts(filtered);
    }
  }, [searchQuery, contacts]);

  const handleAddContact = async () => {
    if (!newContact.name || !newContact.phone) {
      toast.error("Inserisci nome e telefono");
      return;
    }
    try {
      await axios.post(`${API}/contacts`, newContact);
      toast.success("Contatto aggiunto!");
      setAddContactOpen(false);
      setNewContact({ name: "", phone: "" });
      fetchContacts();
    } catch (error) {
      toast.error("Errore nell'aggiunta del contatto");
    }
  };

  const handleDeleteContact = async () => {
    if (!deleteContactId) return;
    
    try {
      await axios.delete(`${API}/contacts/${deleteContactId}`);
      toast.success("Contatto eliminato!");
      setDeleteContactId(null);
      fetchContacts();
    } catch (error) {
      toast.error("Errore nell'eliminazione del contatto");
    }
  };

  const handleImportVCF = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await axios.post(`${API}/contacts/import-vcf`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(`Importati ${response.data.imported} contatti!`);
      setImportOpen(false);
      fetchContacts();
    } catch (error) {
      toast.error("Errore nell'importazione");
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleImportVCF(e.dataTransfer.files[0]);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8 lg:p-12 relative noise-overlay">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div className="flex items-center gap-4">
          <Button
            onClick={() => navigate("/")}
            variant="ghost"
            size="icon"
            className="h-10 w-10 rounded-full"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              Gestione Contatti
            </h1>
            <p className="text-muted-foreground mt-1">
              {contacts.length} contatt{contacts.length === 1 ? 'o' : 'i'} total{contacts.length === 1 ? 'e' : 'i'}
            </p>
          </div>
        </div>
        <div className="flex gap-2 w-full md:w-auto">
          <Dialog open={addContactOpen} onOpenChange={setAddContactOpen}>
            <DialogTrigger asChild>
              <Button className="flex-1 md:flex-none bg-primary text-primary-foreground hover:bg-primary/90 rounded-full h-12">
                <Plus className="h-4 w-4 mr-2" />
                Aggiungi Contatto
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border sm:max-w-[425px] w-[95vw]">
              <DialogHeader>
                <DialogTitle>Nuovo Contatto</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Nome</Label>
                  <Input 
                    placeholder="Mario Rossi"
                    value={newContact.name}
                    onChange={(e) => setNewContact({...newContact, name: e.target.value})}
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefono</Label>
                  <Input 
                    placeholder="+39..."
                    value={newContact.phone}
                    onChange={(e) => setNewContact({...newContact, phone: e.target.value})}
                    className="h-12"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={handleAddContact} className="bg-primary text-primary-foreground rounded-full h-12 w-full sm:w-auto">
                  Aggiungi
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={importOpen} onOpenChange={setImportOpen}>
            <DialogTrigger asChild>
              <Button variant="secondary" className="flex-1 md:flex-none rounded-full h-12">
                <Upload className="h-4 w-4 mr-2" />
                Importa VCF
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border sm:max-w-[425px] w-[95vw]">
              <DialogHeader>
                <DialogTitle>Importa Contatti</DialogTitle>
              </DialogHeader>
              <div 
                className={`drop-zone text-center cursor-pointer ${dragActive ? 'active' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => document.getElementById('vcf-input').click()}
              >
                <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">Trascina il file VCF qui</p>
                <p className="text-sm text-muted-foreground/60 mt-1">oppure clicca per selezionare</p>
                <input 
                  id="vcf-input"
                  type="file" 
                  accept=".vcf"
                  className="hidden"
                  onChange={(e) => e.target.files[0] && handleImportVCF(e.target.files[0])}
                />
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input 
            type="text"
            placeholder="Cerca per nome o telefono..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 h-14 text-lg bg-card border-border"
          />
        </div>
      </div>

      {/* Contacts List */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            {searchQuery ? `Risultati (${filteredContacts.length})` : 'Tutti i Contatti'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredContacts.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{searchQuery ? "Nessun contatto trovato" : "Nessun contatto registrato"}</p>
              <p className="text-sm mt-1">
                {searchQuery ? "Prova con un'altra ricerca" : "Aggiungi il primo contatto per iniziare"}
              </p>
            </div>
          ) : (
            <ScrollArea className="h-[calc(100vh-24rem)]">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pr-4">
                {filteredContacts.map((contact, index) => (
                  <div 
                    key={contact.id}
                    className="p-5 rounded-xl bg-secondary/50 border border-border card-hover animate-fadeIn"
                    style={{ animationDelay: `${index * 30}ms` }}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-lg truncate">{contact.name}</h3>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                          <Phone className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{contact.phone}</span>
                        </div>
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setDeleteContactId(contact.id)}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10 h-9 w-9 flex-shrink-0 ml-2"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Calendar className="h-3 w-3" />
                      <span>
                        Aggiunto {new Date(contact.created_at).toLocaleDateString('it-IT', { 
                          day: 'numeric', 
                          month: 'short', 
                          year: 'numeric' 
                        })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteContactId} onOpenChange={() => setDeleteContactId(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questo contatto?</AlertDialogTitle>
            <AlertDialogDescription>
              Questa azione non può essere annullata. Il contatto verrà eliminato permanentemente.
              I debiti associati a questo contatto non verranno eliminati.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Annulla</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteContact}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-full"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
